import React from 'react';
import toast from 'react-hot-toast';
import styled from 'styled-components';

import { Button } from '../../../components/ui/Button';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import { Modal } from '../../../components/ui/Modal';
import {
  useArchiveQrShare,
  useQrShare,
  useRevokeQrShare,
  useRotateQrShareToken,
  useUpdateQrShare,
} from '../../../hooks/useApi';
import type { QrShareDetail } from '../../../types/api';
import { QrShareResultPanel } from './QrShareResultPanel';
import { QrShareStatusBadge } from './QrShareStatusBadge';

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);

  & + & {
    margin-top: var(--space-lg);
  }
`;

const SectionTitle = styled.h3`
  margin: 0;
  color: var(--text-primary);
  font-size: var(--font-size-base);
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: var(--text-primary);
  font-size: var(--font-size-sm);
  font-weight: 600;
`;

const TextInput = styled.input`
  padding: var(--space-sm);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: var(--radius-sm, 6px);
  background: var(--surface, #fff);
  color: var(--text-primary);
  font-size: var(--font-size-base);
  font-weight: 400;
`;

const Toggle = styled.label`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  color: var(--text-primary);
  font-size: var(--font-size-sm);
`;

const FileList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 14rem;
  overflow-y: auto;
`;

const FileRow = styled.li<{ $available: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-sm);
  padding: var(--space-xs) var(--space-sm);
  border-radius: var(--radius-sm, 6px);
  background: var(--surface-strong, #f8fafc);
  color: ${(props) =>
    props.$available ? 'var(--text-primary)' : 'var(--text-muted)'};
  font-size: var(--font-size-sm);
`;

const FileName = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
`;

const DangerActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-xs);
`;

const Note = styled.p`
  margin: 0;
  color: var(--text-muted);
  font-size: var(--font-size-sm);
  line-height: 1.5;
`;

const ErrorMessage = styled.p`
  margin: 0;
  color: var(--danger-600, #dc2626);
  font-size: var(--font-size-sm);
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--space-xs);
`;

export interface QrShareDetailModalProps {
  shareId: string;
  onClose: () => void;
}

export const QrShareDetailModal: React.FC<QrShareDetailModalProps> = ({
  shareId,
  onClose,
}) => {
  const { data: share, isLoading } = useQrShare(shareId);

  return (
    <Modal isOpen onClose={onClose} title="Gestionar QR compartido" size="lg">
      {isLoading || !share ? (
        <LoadingSpinner message="Cargando paquete..." />
      ) : (
        <QrShareDetailBody share={share} onClose={onClose} />
      )}
    </Modal>
  );
};

interface QrShareDetailBodyProps {
  share: QrShareDetail;
  onClose: () => void;
}

const QrShareDetailBody: React.FC<QrShareDetailBodyProps> = ({
  share,
  onClose,
}) => {
  const [name, setName] = React.useState(share.name);
  const [expiresAt, setExpiresAt] = React.useState(
    share.expires_at ? share.expires_at.slice(0, 10) : '',
  );
  const [allowDownload, setAllowDownload] = React.useState(
    share.allow_download,
  );
  const [showRestaurantName, setShowRestaurantName] = React.useState(
    share.show_restaurant_name,
  );

  const updateShare = useUpdateQrShare();
  const revokeShare = useRevokeQrShare();
  const rotateToken = useRotateQrShareToken();
  const archiveShare = useArchiveQrShare();

  const isEditable = share.status === 'active' || share.status === 'expired';
  const isBusy =
    updateShare.isPending ||
    revokeShare.isPending ||
    rotateToken.isPending ||
    archiveShare.isPending;

  const handleSave = React.useCallback(() => {
    updateShare.mutate(
      {
        shareId: share.id,
        payload: {
          name: name.trim(),
          allow_download: allowDownload,
          show_restaurant_name: showRestaurantName,
          // Explicit null clears the expiry; the backend applies only sent fields.
          expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
        },
      },
      { onSuccess: () => toast.success('Paquete actualizado') },
    );
  }, [
    allowDownload,
    expiresAt,
    name,
    share.id,
    showRestaurantName,
    updateShare,
  ]);

  const handleRemoveFile = React.useCallback(
    (removedItemId: string) => {
      const remaining = share.items
        .filter((item) => item.id !== removedItemId)
        .map((item) => ({
          source_type: item.source_type,
          source_id: item.source_id,
        }));

      if (remaining.length === 0) {
        toast.error('El paquete debe incluir al menos un archivo.');
        return;
      }

      updateShare.mutate(
        { shareId: share.id, payload: { files: remaining } },
        { onSuccess: () => toast.success('Archivo quitado del paquete') },
      );
    },
    [share.id, share.items, updateShare],
  );

  const handleRevoke = React.useCallback(() => {
    revokeShare.mutate(share.id, {
      onSuccess: () => {
        toast.success('Enlace revocado');
        onClose();
      },
    });
  }, [onClose, revokeShare, share.id]);

  const handleRotate = React.useCallback(() => {
    rotateToken.mutate(share.id, {
      onSuccess: () =>
        toast.success('Nuevo enlace generado. Vuelve a imprimir el QR.'),
    });
  }, [rotateToken, share.id]);

  const handleArchive = React.useCallback(() => {
    archiveShare.mutate(share.id, {
      onSuccess: () => {
        toast.success('Paquete archivado');
        onClose();
      },
    });
  }, [archiveShare, onClose, share.id]);

  const mutationError =
    updateShare.error ??
    revokeShare.error ??
    rotateToken.error ??
    archiveShare.error;

  return (
    <>
      <Section>
        <SectionTitle>
          Enlace <QrShareStatusBadge status={share.status} />
        </SectionTitle>
        <QrShareResultPanel share={share} />
      </Section>

      <Section>
        <SectionTitle>Configuración</SectionTitle>
        <Field htmlFor="qr-detail-name">
          Nombre del paquete
          <TextInput
            id="qr-detail-name"
            value={name}
            maxLength={100}
            disabled={!isEditable}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field htmlFor="qr-detail-expires">
          Vencimiento (vacío = sin vencimiento)
          <TextInput
            id="qr-detail-expires"
            type="date"
            value={expiresAt}
            disabled={!isEditable}
            onChange={(event) => setExpiresAt(event.target.value)}
          />
        </Field>
        <Toggle>
          <input
            type="checkbox"
            checked={allowDownload}
            disabled={!isEditable}
            onChange={(event) => setAllowDownload(event.target.checked)}
          />
          Permitir descargas
        </Toggle>
        <Toggle>
          <input
            type="checkbox"
            checked={showRestaurantName}
            disabled={!isEditable}
            onChange={(event) => setShowRestaurantName(event.target.checked)}
          />
          Mostrar nombre del restaurante
        </Toggle>
      </Section>

      <Section>
        <SectionTitle>
          Archivos incluidos ({share.available_item_count}/{share.item_count})
        </SectionTitle>
        <FileList>
          {share.items.map((item) => (
            <FileRow key={item.id} $available={item.available}>
              <FileName>
                {item.name}
                {!item.available && ' · no disponible'}
              </FileName>
              {isEditable && (
                <Button
                  variant="tertiary"
                  size="sm"
                  disabled={isBusy}
                  onClick={() => handleRemoveFile(item.id)}
                >
                  Quitar
                </Button>
              )}
            </FileRow>
          ))}
        </FileList>
        <Note>
          Para añadir archivos nuevos, crea un paquete con el alcance
          correspondiente.
        </Note>
      </Section>

      {isEditable && (
        <Section>
          <SectionTitle>Acciones</SectionTitle>
          <DangerActions>
            <Button
              variant="secondary"
              size="sm"
              loading={rotateToken.isPending}
              disabled={isBusy}
              onClick={handleRotate}
            >
              Generar nuevo enlace
            </Button>
            <Button
              variant="secondary"
              size="sm"
              loading={archiveShare.isPending}
              disabled={isBusy}
              onClick={handleArchive}
            >
              Archivar
            </Button>
            <Button
              variant="tertiary"
              size="sm"
              loading={revokeShare.isPending}
              disabled={isBusy}
              onClick={handleRevoke}
            >
              Revocar enlace
            </Button>
          </DangerActions>
          <Note>
            Al generar un nuevo enlace, el QR anterior deja de funcionar de
            inmediato.
          </Note>
        </Section>
      )}

      {mutationError && (
        <ErrorMessage role="alert">{mutationError.message}</ErrorMessage>
      )}

      <FooterActions style={{ marginTop: 'var(--space-lg)' }}>
        <Button variant="tertiary" onClick={onClose}>
          Cerrar
        </Button>
        {isEditable && (
          <Button
            variant="primary"
            loading={updateShare.isPending}
            disabled={isBusy || name.trim().length === 0}
            onClick={handleSave}
          >
            Guardar cambios
          </Button>
        )}
      </FooterActions>
    </>
  );
};
